import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Languages,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Search,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Wand2,
  Save,
  Eye,
  EyeOff,
} from "lucide-react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const FIELDS = [
  { key: "name", label: "Nom" },
  { key: "short_description", label: "Description courte" },
  { key: "description", label: "Description longue" },
  { key: "ingredients", label: "Ingrédients" },
  { key: "storage_instructions", label: "Conservation" },
  { key: "reheating_instructions", label: "Réchauffage" },
];

/**
 * Page de révision côte-à-côte des traductions produits FR / DE / IT.
 *
 * Vue liste : tous les produits avec stats DE/IT (✓ complet, partiel, ✗ absent).
 * Vue détail : 3 colonnes (FR readonly, DE editable, IT editable) + boutons :
 *  - Enregistrer (PUT /api/products/{id})
 *  - Re-traduire (POST /api/admin/translate-products body={product_ids: [id]} force=true)
 *  - Précédent / Suivant pour naviguer entre produits
 */
function safeErrorMessage(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length > 0 && typeof d[0]?.msg === "string") {
    return d[0].msg;
  }
  return fallback;
}

export default function Translations() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);  // full product details
  const [statusRows, setStatusRows] = useState([]);  // from /admin/translations-status
  const [stats, setStats] = useState({ total: 0, de_complete: 0, it_complete: 0 });
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");  // ALL | TODO | DONE
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState({});  // {field_de: val, field_it: val}
  const [saving, setSaving] = useState(false);
  const [retranslating, setRetranslating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/admin/translations-status"),
        api.get("/products"),
      ]);
      setStatusRows(s.data.products || []);
      setStats(s.data.stats || {});
      setProducts(p.data || []);
    } catch (e) {
      toast.error(safeErrorMessage(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const productsMap = useMemo(
    () => Object.fromEntries(products.map((p) => [p.id, p])),
    [products]
  );

  const filteredRows = useMemo(() => {
    const q = query.toLowerCase().trim();
    return statusRows.filter((r) => {
      const matchQ =
        !q ||
        r.name.toLowerCase().includes(q) ||
        (r.sku || "").toLowerCase().includes(q);
      const matchStatus =
        statusFilter === "ALL" ||
        (statusFilter === "TODO" && (!r.de_complete || !r.it_complete) && r.fr_fields_count > 0) ||
        (statusFilter === "DONE" && r.de_complete && r.it_complete);
      return matchQ && matchStatus;
    });
  }, [statusRows, query, statusFilter]);

  const selectedRow = selectedId ? statusRows.find((r) => r.product_id === selectedId) : null;
  const selectedProduct = selectedId ? productsMap[selectedId] : null;

  // Open detail view
  const openDetail = (productId) => {
    const p = productsMap[productId];
    if (!p) return;
    const d = {};
    for (const f of FIELDS) {
      d[`${f.key}_de`] = p[`${f.key}_de`] || "";
      d[`${f.key}_it`] = p[`${f.key}_it`] || "";
    }
    setDraft(d);
    setSelectedId(productId);
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDraft({});
  };

  // Navigation prev/next
  const filteredOrder = filteredRows.map((r) => r.product_id);
  const currentIdx = selectedId ? filteredOrder.indexOf(selectedId) : -1;
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx >= 0 && currentIdx < filteredOrder.length - 1;
  const goPrev = () => hasPrev && openDetail(filteredOrder[currentIdx - 1]);
  const goNext = () => hasNext && openDetail(filteredOrder[currentIdx + 1]);

  const updateDraft = (field, lang, value) => {
    setDraft((prev) => ({ ...prev, [`${field}_${lang}`]: value }));
  };

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await api.put(`/products/${selectedId}`, draft);
      toast.success("Traductions enregistrées");
      await load();
    } catch (e) {
      toast.error(safeErrorMessage(e, "Erreur d'enregistrement"));
    } finally {
      setSaving(false);
    }
  };

  const retranslate = async () => {
    if (!selectedId) return;
    setRetranslating(true);
    try {
      const { data } = await api.post(
        `/admin/translate-products?force=true&only_visible=false`,
        { product_ids: [selectedId] }
      );
      if (data.translated_count > 0) {
        toast.success("Produit re-traduit");
        // Reload then re-open detail with fresh data
        await load();
        // re-set draft from updated product
        const updated = await api.get(`/products/${selectedId}`);
        const d = {};
        for (const f of FIELDS) {
          d[`${f.key}_de`] = updated.data[`${f.key}_de`] || "";
          d[`${f.key}_it`] = updated.data[`${f.key}_it`] || "";
        }
        setDraft(d);
      } else if (data.failed_count > 0) {
        toast.error("Échec de la re-traduction. Réessayez plus tard.");
      } else {
        toast.info("Rien à re-traduire (déjà à jour ?)");
      }
    } catch (e) {
      toast.error(safeErrorMessage(e, "Erreur de re-traduction"));
    } finally {
      setRetranslating(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-[#575A57]" data-testid="translations-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Chargement des produits…
      </div>
    );
  }

  // ============ Vue détail (3 colonnes) ============
  if (selectedId && selectedProduct && selectedRow) {
    return (
      <div className="space-y-4" data-testid="translations-detail">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={closeDetail}
              data-testid="translations-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Liste
            </Button>
            <div>
              <h2 className="font-heading text-lg font-semibold text-[#1A1C1A]">
                {selectedProduct.name}
              </h2>
              <p className="text-xs text-[#575A57] font-mono">{selectedProduct.sku}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={goPrev}
              disabled={!hasPrev}
              data-testid="translations-prev-btn"
            >
              <ChevronLeft className="w-4 h-4" /> Précédent
            </Button>
            <span className="text-xs text-[#575A57]">
              {currentIdx + 1} / {filteredOrder.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={goNext}
              disabled={!hasNext}
              data-testid="translations-next-btn"
            >
              Suivant <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Status badges */}
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <Badge className="bg-[#E8EEF6] text-[#1F3A66] border border-[#A8BBD6]">
            FR · {selectedRow.fr_fields_count} champs remplis
          </Badge>
          <Badge
            className={
              selectedRow.de_complete
                ? "bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6]"
                : "bg-[#FBF4E9] text-[#8C6B2F] border border-[#E8D3A8]"
            }
          >
            DE · {selectedRow.de_complete ? "complet" : `${selectedRow.de_missing_fields.length} manquant(s)`}
          </Badge>
          <Badge
            className={
              selectedRow.it_complete
                ? "bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6]"
                : "bg-[#FBF4E9] text-[#8C6B2F] border border-[#E8D3A8]"
            }
          >
            IT · {selectedRow.it_complete ? "complet" : `${selectedRow.it_missing_fields.length} manquant(s)`}
          </Badge>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="translations-save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Enregistrer
          </Button>
          <Button
            variant="outline"
            onClick={retranslate}
            disabled={retranslating}
            data-testid="translations-retranslate-btn"
            title="Re-générer DE + IT pour ce produit via Claude Sonnet"
          >
            {retranslating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1.5" />}
            Re-traduire (Claude)
          </Button>
        </div>

        {/* 3 columns */}
        <div className="border border-[#E5E5E0] rounded-md overflow-hidden bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-[#E5E5E0]">
            {/* FR readonly */}
            <div>
              <div className="bg-[#F4F4F0] px-3 py-2 border-b border-[#E5E5E0] flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">
                  🇫🇷 Français (source)
                </span>
              </div>
              <div className="p-3 space-y-3">
                {FIELDS.map((f) => (
                  <FieldRow key={`fr-${f.key}`} label={f.label} value={selectedProduct[f.key] || ""} readOnly />
                ))}
              </div>
            </div>
            {/* DE editable */}
            <div>
              <div className="bg-[#F4F4F0] px-3 py-2 border-b border-[#E5E5E0] flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">
                  🇩🇪 Deutsch
                </span>
                {selectedRow.de_complete && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#1F4E3D]" />
                )}
              </div>
              <div className="p-3 space-y-3">
                {FIELDS.map((f) => {
                  const frEmpty = !(selectedProduct[f.key] || "").trim();
                  return (
                    <FieldRow
                      key={`de-${f.key}`}
                      label={f.label}
                      value={draft[`${f.key}_de`] || ""}
                      onChange={(v) => updateDraft(f.key, "de", v)}
                      dimmed={frEmpty}
                      testid={`field-${f.key}-de`}
                    />
                  );
                })}
              </div>
            </div>
            {/* IT editable */}
            <div>
              <div className="bg-[#F4F4F0] px-3 py-2 border-b border-[#E5E5E0] flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">
                  🇮🇹 Italiano
                </span>
                {selectedRow.it_complete && (
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#1F4E3D]" />
                )}
              </div>
              <div className="p-3 space-y-3">
                {FIELDS.map((f) => {
                  const frEmpty = !(selectedProduct[f.key] || "").trim();
                  return (
                    <FieldRow
                      key={`it-${f.key}`}
                      label={f.label}
                      value={draft[`${f.key}_it`] || ""}
                      onChange={(v) => updateDraft(f.key, "it", v)}
                      dimmed={frEmpty}
                      testid={`field-${f.key}-it`}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============ Vue liste ============
  const todoCount = statusRows.filter((r) => r.fr_fields_count > 0 && (!r.de_complete || !r.it_complete)).length;
  const doneCount = statusRows.filter((r) => r.de_complete && r.it_complete).length;

  return (
    <div className="space-y-6" data-testid="translations-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Languages className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-lg font-semibold text-[#1A1C1A]">
            Révision des traductions FR · DE · IT
          </h2>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/produits")}
          data-testid="translations-back-to-products"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux produits
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total produits" value={stats.total} color="#1F4E3D" />
        <StatCard label="DE complets" value={`${stats.de_complete} / ${stats.total}`} color="#1F4E3D" />
        <StatCard label="IT complets" value={`${stats.it_complete} / ${stats.total}`} color="#1F4E3D" />
        <StatCard label="À traiter" value={todoCount} color={todoCount > 0 ? "#C05C47" : "#1F4E3D"} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#575A57]" />
          <Input
            placeholder="Rechercher par nom ou SKU…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9 bg-white"
            data-testid="translations-search"
          />
        </div>
        <div className="inline-flex items-center rounded-md border border-[#E5E5E0] bg-white p-0.5">
          {[
            { v: "ALL", label: "Tous", count: stats.total },
            { v: "TODO", label: "À traiter", count: todoCount },
            { v: "DONE", label: "Complets", count: doneCount },
          ].map(({ v, label, count }) => (
            <button
              key={v}
              type="button"
              onClick={() => setStatusFilter(v)}
              data-testid={`translations-filter-${v.toLowerCase()}`}
              className={`px-3 py-1.5 text-xs rounded transition-colors flex items-center gap-1.5 ${
                statusFilter === v ? "bg-[#1F4E3D] text-white" : "text-[#575A57] hover:bg-[#F4F4F0]"
              }`}
            >
              {label}
              <span
                className={`ml-0.5 text-[10px] font-mono ${
                  statusFilter === v ? "text-white/80" : "text-[#A3A6A3]"
                }`}
              >
                {count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Produit</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Boutique</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">FR</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">🇩🇪 DE</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">🇮🇹 IT</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="translations-table-body">
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#575A57] py-8">
                  Aucun produit trouvé
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((r) => (
                <TableRow key={r.product_id} data-testid={`translations-row-${r.product_id}`}>
                  <TableCell>
                    <button
                      className="text-left hover:text-[#1F4E3D]"
                      onClick={() => openDetail(r.product_id)}
                    >
                      <div className="font-medium text-[#1A1C1A]">{r.name}</div>
                      <div className="text-[10px] font-mono text-[#A3A6A3]">{r.sku}</div>
                    </button>
                  </TableCell>
                  <TableCell>
                    {r.online_visible ? (
                      <Eye className="w-3.5 h-3.5 text-[#1F4E3D]" />
                    ) : (
                      <EyeOff className="w-3.5 h-3.5 text-[#A3A6A3]" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {r.fr_fields_count} champs
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <LangBadge
                      complete={r.de_complete}
                      missingCount={r.de_missing_fields.length}
                      frCount={r.fr_fields_count}
                    />
                  </TableCell>
                  <TableCell>
                    <LangBadge
                      complete={r.it_complete}
                      missingCount={r.it_missing_fields.length}
                      frCount={r.fr_fields_count}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetail(r.product_id)}
                      data-testid={`translations-edit-${r.product_id}`}
                    >
                      Réviser
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-md p-3" data-testid={`translations-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#575A57]">{label}</div>
      <div className="text-lg font-mono font-semibold mt-0.5" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function LangBadge({ complete, missingCount, frCount }) {
  if (frCount === 0) {
    return <span className="text-[#A3A6A3] text-xs">—</span>;
  }
  if (complete) {
    return (
      <Badge className="bg-[#E8F2EC] text-[#1F4E3D] border border-[#A8C9B6] text-[10px] gap-1">
        <CheckCircle2 className="w-3 h-3" /> Complet
      </Badge>
    );
  }
  if (missingCount === frCount) {
    return (
      <Badge className="bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0] text-[10px] gap-1">
        <AlertCircle className="w-3 h-3" /> Absent
      </Badge>
    );
  }
  return (
    <Badge className="bg-[#FBF4E9] text-[#8C6B2F] border border-[#E8D3A8] text-[10px] gap-1">
      <AlertCircle className="w-3 h-3" /> {missingCount} manq.
    </Badge>
  );
}

function FieldRow({ label, value, onChange, readOnly, dimmed, testid }) {
  const isLong = (value || "").length > 80 || (value || "").includes("\n");
  return (
    <div className={dimmed ? "opacity-50" : ""}>
      <label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">
        {label}
      </label>
      {readOnly ? (
        <div
          className={`text-sm whitespace-pre-wrap rounded border px-2 py-1.5 bg-[#FAFAF7] border-[#E5E5E0] min-h-[36px] ${
            isLong ? "" : ""
          }`}
        >
          {value || <span className="text-[#A3A6A3] italic">— vide —</span>}
        </div>
      ) : isLong ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={Math.min(8, Math.max(2, Math.ceil((value || "").length / 60)))}
          className="text-sm"
          data-testid={testid}
        />
      ) : (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="text-sm"
          data-testid={testid}
        />
      )}
    </div>
  );
}
